// Debt payoff — Avalanche (highest rate first) vs Snowball (smallest balance first).
import { round2 } from "./finance-math";

function simulate(debts, extraPayment, strategy) {
  // debts: [{ name, balance, rate(%), minPayment }]
  let list = debts.map((d) => ({
    name: d.name,
    balance: Math.max(0, Number(d.balance) || 0),
    rate: Math.max(0, Number(d.rate) || 0) / 100 / 12,
    min: Math.max(0, Number(d.minPayment) || 0),
  })).filter((d) => d.balance > 0);

  let months = 0;
  let totalInterest = 0;
  const startBalance = list.reduce((a, d) => a + d.balance, 0);
  const series = [{ month: 0, balance: round2(startBalance) }];
  const maxMonths = 1200;

  while (list.some((d) => d.balance > 0.01) && months < maxMonths) {
    months += 1;
    // accrue interest
    for (const d of list) {
      if (d.balance > 0) {
        const interest = d.balance * d.rate;
        d.balance += interest;
        totalInterest += interest;
      }
    }
    // order targets
    const active = list.filter((d) => d.balance > 0.01);
    active.sort((a, b) => (strategy === "avalanche" ? b.rate - a.rate : a.balance - b.balance));

    let budget = list.reduce((a, d) => a + (d.balance > 0.01 ? d.min : 0), 0) + extraPayment;
    // pay minimums first
    for (const d of active) {
      const pay = Math.min(d.min, d.balance);
      d.balance -= pay;
      budget -= pay;
    }
    // funnel remaining budget to the target debt(s) in order
    for (const d of active) {
      if (budget <= 0) break;
      if (d.balance <= 0.01) continue;
      const pay = Math.min(budget, d.balance);
      d.balance -= pay;
      budget -= pay;
    }
    const bal = list.reduce((a, d) => a + Math.max(0, d.balance), 0);
    series.push({ month: months, balance: round2(bal) });
    if (bal <= 0.01) break;
  }

  return {
    months,
    years: round2(months / 12),
    totalInterest: round2(totalInterest),
    totalPaid: round2(startBalance + totalInterest),
    series,
  };
}

export function debtPayoff({ debts, extraPayment = 0 }) {
  const extra = Math.max(0, Number(extraPayment) || 0);
  const valid = (debts || []).filter((d) => Number(d.balance) > 0);
  const avalanche = simulate(valid, extra, "avalanche");
  const snowball = simulate(valid, extra, "snowball");
  const totalDebt = valid.reduce((a, d) => a + (Number(d.balance) || 0), 0);
  const best = avalanche.totalInterest <= snowball.totalInterest ? "avalanche" : "snowball";
  return {
    totalDebt: round2(totalDebt),
    avalanche,
    snowball,
    recommended: best,
    interestSavedVsSnowball: round2(snowball.totalInterest - avalanche.totalInterest),
  };
}
